import { BENCHMARK_GUARANTEE_PERCENTAGE } from "@datanac/datanac-api-toolkit/dist/arcplc/ArcPlcHelper";
import { Alert, Paper, Stack, Typography } from "@mui/material";

import { formatNumber, formatPercentage, formatPrice } from "../../../utility/formatters";
import { OperatorBox } from "./ArcCoOperatorBox";
import ArcCoPaymentSummaryBox from "./ArcCoPaymentSummaryBox";

const ArcCoStepsContent = ({ arcCoCalculation, combinedCalculation }) => {
  const {
    benchmark_price,
    benchmark_yield,
    benchmark_revenue,
    benchmark_guarantee,
    actual_revenue,
    payment,
    maxPayment,
    isPaymentCapped,
    totalFarmPayment,
    share_percent,
    MAX_PAYMENT_THRESHOLD,
    BASE_ACRE_PERCENTAGE,
  } = arcCoCalculation;

  const isPaymentTriggered = actual_revenue < benchmark_guarantee;

  return (
    <Stack className="arc-plc-steps-content">
      {/* Step 1: Benchmark Revenue Calculation */}
      <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            borderBottom: 1,
            borderColor: "colors.borderLightGray",
            padding: "14px 18px",
            textAlign: "left",
            "& span": {
              color: "text.secondary",
            },
          }}
        >
          <span>Step 1:</span> Calculate Benchmark Revenue
        </Typography>

        <Stack
          sx={{
            padding: "20px 18px",
            flexDirection: "row",
            alignItems: "stretch",
            flexWrap: "wrap",
          }}
        >
          <ArcCoPaymentSummaryBox title="Benchmark Price" value={benchmark_price} formatOptions={{ places: 2 }} />
          <OperatorBox>×</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="Benchmark Yield"
            value={benchmark_yield}
            formatType="number"
            formatOptions={{ places: 1 }}
          />
          <OperatorBox>=</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="Benchmark Revenue"
            value={benchmark_revenue}
            formatOptions={{ places: 2 }}
            valueColor="success.main"
          />
        </Stack>
      </Paper>

      {/* Step 2: Benchmark Guarantee Calculation */}
      <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            borderBottom: 1,
            borderColor: "colors.borderLightGray",
            padding: "14px 18px",
            textAlign: "left",
            "& span": {
              color: "text.secondary",
            },
          }}
        >
          <span>Step 2:</span> Calculate Benchmark Guarantee ({formatNumber(BENCHMARK_GUARANTEE_PERCENTAGE * 100, 0)}%
          of Benchmark Revenue)
        </Typography>

        <Stack
          sx={{
            padding: "20px 18px",
            flexDirection: "row",
            alignItems: "stretch",
            flexWrap: "wrap",
          }}
        >
          <ArcCoPaymentSummaryBox title="Benchmark Revenue" value={benchmark_revenue} formatOptions={{ places: 2 }} />
          <OperatorBox>×</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="COVERAGE"
            value={BENCHMARK_GUARANTEE_PERCENTAGE}
            formatType="percentage"
            formatOptions={{ places: 0 }}
          />
          <OperatorBox>=</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="Guarantee"
            value={benchmark_guarantee}
            formatOptions={{ places: 2 }}
            valueColor="success.main"
          />
        </Stack>
      </Paper>

      {isPaymentTriggered && (
        <>
          {/* Step 3 Payment Calculation */}
          <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                borderBottom: 1,
                borderColor: "colors.borderLightGray",
                padding: "14px 18px",
                textAlign: "left",
                "& span": {
                  color: "text.secondary",
                },
              }}
            >
              <span>Step 3:</span> Calculate Payment (85% of Base Acres)
            </Typography>

            <Stack
              sx={{
                padding: "20px 18px",
                flexDirection: "row",
                alignItems: "stretch",
                flexWrap: "wrap",
              }}
            >
              <OperatorBox>(</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="Benchmark Guarantee"
                value={benchmark_guarantee}
                formatOptions={{ places: 2 }}
              />
              <OperatorBox>-</OperatorBox>
              <ArcCoPaymentSummaryBox title="Actual Revenue" value={actual_revenue} formatOptions={{ places: 2 }} />
              <OperatorBox>)</OperatorBox>
              <OperatorBox>×</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="Base Acres"
                value={combinedCalculation?.base_acreage}
                formatType="number"
                formatOptions={{ places: 0 }}
              />
              <OperatorBox>×</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="COVERAGE"
                value={BASE_ACRE_PERCENTAGE || 0.85}
                formatType="percentage"
                formatOptions={{ places: 0 }}
              />

              <OperatorBox>=</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="Calculated Payment"
                value={payment}
                formatOptions={{ places: 0 }}
                valueColor="success.main"
              />
            </Stack>
          </Paper>

          {/* Step 4 Payment Cap Check */}
          <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                borderBottom: 1,
                borderColor: "colors.borderLightGray",
                padding: "14px 18px",
                textAlign: "left",
                "& span": {
                  color: "text.secondary",
                },
              }}
            >
              <span>Step 4:</span> Apply Payment Cap (Maximum {formatPercentage(MAX_PAYMENT_THRESHOLD || 0.1, 0)} of
              Benchmark Revenue)
            </Typography>

            <Stack
              sx={{
                padding: "20px 18px",
                flexDirection: "row",
                alignItems: "stretch",
                flexWrap: "wrap",
              }}
            >
              <ArcCoPaymentSummaryBox
                title="Benchmark Revenue"
                value={benchmark_revenue}
                formatOptions={{ places: 2 }}
              />
              <OperatorBox>×</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="CAP RATE"
                value={MAX_PAYMENT_THRESHOLD || 0.1}
                formatType="percentage"
                formatOptions={{ places: 0 }}
              />
              <OperatorBox>×</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="Base Acres"
                value={combinedCalculation?.base_acreage}
                formatType="number"
                formatOptions={{ places: 0 }}
              />
              <OperatorBox>×</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="COVERAGE"
                value={BASE_ACRE_PERCENTAGE || 0.85}
                formatType="percentage"
                formatOptions={{ places: 0 }}
              />

              <OperatorBox>=</OperatorBox>
              <ArcCoPaymentSummaryBox
                title="Maximum Payment"
                value={maxPayment}
                formatOptions={{ places: 0 }}
                valueColor="success.main"
              />
            </Stack>

            {isPaymentCapped && (
              <Alert
                severity="warning"
                sx={{
                  borderRadius: "0.5rem",
                  border: 1,
                  margin: "0 18px 20px",
                  width: "fit-content",
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  color: "warning.dark",
                }}
              >
                Payment is capped at the maximum allowable amount of {formatPrice(maxPayment, 0)}
              </Alert>
            )}
          </Paper>

          {/* Step 5: Final Payment */}
          {share_percent < 1 && (
            <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  borderBottom: 1,
                  borderColor: "colors.borderLightGray",
                  padding: "14px 18px",
                  textAlign: "left",
                  "& span": {
                    color: "text.secondary",
                  },
                }}
              >
                <span>Step 5:</span> Apply Share Percent
              </Typography>

              <Stack
                sx={{
                  padding: "20px 18px",
                  flexDirection: "row",
                  alignItems: "stretch",
                  flexWrap: "wrap",
                }}
              >
                <ArcCoPaymentSummaryBox
                  title={isPaymentCapped ? "Maximum Payment" : "Expected Payment"}
                  value={totalFarmPayment}
                  formatOptions={{ places: 0 }}
                />
                <OperatorBox>×</OperatorBox>
                <ArcCoPaymentSummaryBox
                  title="Share Percent"
                  value={share_percent}
                  formatType="percentage"
                  formatOptions={{ places: 0 }}
                />
                <OperatorBox>=</OperatorBox>
                <ArcCoPaymentSummaryBox
                  title="Expected Share"
                  value={totalFarmPayment * share_percent}
                  formatOptions={{ places: 0 }}
                  valueColor="success.main"
                />
              </Stack>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
};

export default ArcCoStepsContent;
